import { useState } from "react"
import { format, addMonths, startOfDay, endOfDay } from "date-fns"
import { Calendar as CalendarIcon, Download, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import { Input } from "@/components/ui/input"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import { api } from "@/lib/api"
import { useChatbots } from '@/lib/queries'

export function Logs() {
    const [startDate, setStartDate] = useState<Date>()
    const [endDate, setEndDate] = useState<Date>()
    const [chatbot, setChatbot] = useState<string>("")
    const { data: chatbots = [] } = useChatbots()
    const [isLoading, setIsLoading] = useState(false)
    const [searchQuery, setSearchQuery] = useState("")

    const handleExport = async () => {
        if (!chatbot || !startDate || !endDate) return

        setIsLoading(true)
        try {
            // Ensure full day range
            const start = startOfDay(startDate).toISOString()
            const end = endOfDay(endDate).toISOString()

            const blob = await api.exportLogs(chatbot, start, end)

            // Generate filename
            const dateStr = format(new Date(), "ddMMyyyy")
            let filename = `ChatLog_${dateStr}.xlsx`
            if (chatbot !== 'all') {
                const botName = chatbots.find(c => c.id === chatbot)?.name || chatbot
                const safeBotName = botName.replace(/[^a-z0-9]/gi, '_')
                filename = `ChatLog_${safeBotName}_${dateStr}.xlsx`
            }

            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = filename
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)
        } catch (error) {
            console.error("Export failed", error)
            alert("Failed to export logs")
        } finally {
            setIsLoading(false)
        }
    }

    const filteredChatbots = chatbots.filter(bot =>
        bot.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        bot.id.toLowerCase().includes(searchQuery.toLowerCase())
    )

    return (
        <div className="p-8 max-w-[1400px]">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">Log File Report</h1>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-8">
                <div className="space-y-2 md:col-span-6">
                    <label className="text-sm font-medium text-gray-900">Select a chatbot</label>
                    <Select value={chatbot} onValueChange={setChatbot} onOpenChange={(open) => { if (!open) setSearchQuery("") }}>
                        <SelectTrigger
                            className="w-full bg-white h-auto min-h-9 text-left [&>span]:line-clamp-none [&>span]:whitespace-normal [&>span]:break-words"
                            onKeyDownCapture={(e) => {
                                if (e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey) {
                                    e.stopPropagation()
                                    e.preventDefault()
                                }
                            }}
                        >
                            <SelectValue placeholder="Select chatbot">
                                {chatbot === 'all'
                                    ? 'All Chatbots'
                                    : chatbot && chatbots.find(b => b.id === chatbot)
                                        ? `${chatbots.find(b => b.id === chatbot)?.name} (${chatbot})`
                                        : undefined}
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent
                            className="w-[var(--radix-select-trigger-width)]"
                            onKeyDownCapture={(e) => {
                                if ((e.target as HTMLElement).tagName === 'INPUT') return;
                                if (e.key.length === 1 && e.key !== " " && !e.ctrlKey && !e.metaKey) {
                                    e.stopPropagation()
                                    e.preventDefault()
                                }
                            }}
                        >
                            <div className="sticky -top-1 z-10 bg-white -mt-1 -mx-1 px-3 py-2 border-b mb-1">
                                <Input
                                    placeholder="Search chatbot..."
                                    value={searchQuery}
                                    onChange={(e) => {
                                        setSearchQuery(e.target.value)
                                        const input = e.target
                                        setTimeout(() => input.focus(), 0)
                                    }}
                                    onKeyDown={(e) => e.stopPropagation()}
                                />
                            </div>
                            <SelectItem value="all">All Chatbots</SelectItem>
                            {filteredChatbots.length === 0 ? (
                                <div className="p-2 text-sm text-center text-gray-500">No chatbots found</div>
                            ) : (
                                filteredChatbots.map(bot => (
                                    <SelectItem key={bot.id} value={bot.id} className="h-auto whitespace-normal break-words py-2 pr-8">
                                        {bot.name} ({bot.id})
                                    </SelectItem>
                                ))
                            )}
                        </SelectContent>
                    </Select>
                </div>

                <div className="space-y-2 md:col-span-3">
                    <label className="text-sm font-medium text-gray-900">Start Date</label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant={"outline"}
                                className={cn(
                                    "w-full justify-between text-left font-normal bg-white",
                                    !startDate && "text-muted-foreground"
                                )}
                            >
                                {startDate ? format(startDate, "MM/dd/yyyy") : <span>MM/DD/YY</span>}
                                <CalendarIcon className="h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={startDate}
                                onSelect={(date) => {
                                    setStartDate(date)
                                    setEndDate(date)
                                }}
                                initialFocus
                                disabled={(date) => date > endOfDay(new Date())}
                            />
                        </PopoverContent>
                    </Popover>
                </div>

                <div className="space-y-2 md:col-span-3">
                    <label className="text-sm font-medium text-gray-900">End Date</label>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant={"outline"}
                                className={cn(
                                    "w-full justify-between text-left font-normal bg-white",
                                    !endDate && "text-muted-foreground"
                                )}
                                disabled={!startDate}
                            >
                                {endDate ? format(endDate, "MM/dd/yyyy") : <span>MM/DD/YY</span>}
                                <CalendarIcon className="h-4 w-4 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                                mode="single"
                                selected={endDate}
                                onSelect={setEndDate}
                                initialFocus
                                disabled={(date) => {
                                    if (!startDate) return true
                                    const maxDate = addMonths(startDate, 3)
                                    const today = endOfDay(new Date())
                                    const effectiveMaxDate = maxDate > today ? today : maxDate;
                                    return date < startDate || date > effectiveMaxDate
                                }}
                            />
                        </PopoverContent>
                    </Popover>
                </div>
            </div>

            <div className="flex justify-end">
                <Button
                    className="bg-[#D91F27] hover:bg-[#b81a21] text-white gap-2 font-medium px-6"
                    onClick={handleExport}
                    disabled={!chatbot || !startDate || !endDate || isLoading}
                >
                    {isLoading ? <Loader2 className="animate-spin" size={18} /> : <Download size={18} />}
                    Export
                </Button>
            </div>
        </div>
    )
}
